import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiErrorMessage } from '../../shared/api/errors';
import { formatPhoneRu } from '../../shared/lib/phone';
import { Button } from '../../shared/ui/Button';
import { TextField, inputClass } from '../../shared/ui/TextField';
import { Card, Spinner } from '../../shared/ui/primitives';
import { setMyClinics, updateMe } from '../auth/api';
import { useSession } from '../auth/session';
import { ClinicMultiSelect } from '../clinics/ClinicMultiSelect';

type Values = { fullName: string; phone: string };

export function ProfilePage() {
  const { user } = useSession();
  const queryClient = useQueryClient();
  const [clinicIds, setClinicIds] = useState<string[]>([]);
  const clinicIdsRef = useRef<string[]>([]);
  clinicIdsRef.current = clinicIds;

  const { control, register, handleSubmit, reset } = useForm<Values>();

  useEffect(() => {
    if (user) {
      reset({ fullName: user.fullName, phone: user.phone });
      setClinicIds(user.clinics.map((c) => c.id));
    }
  }, [user, reset]);

  const save = useMutation({
    mutationFn: async (values: Values) => {
      await updateMe(values);
      return setMyClinics(clinicIdsRef.current);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });

  if (!user) return <Spinner />;

  return (
    <section className="mx-auto max-w-lg space-y-5">
      <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">Мой профиль</h1>

      <Card className="p-5 sm:p-6">
        <form onSubmit={handleSubmit((v) => save.mutate(v))} className="space-y-4">
          <TextField label="E-mail" value={user.email} readOnly className="opacity-60" />
          <TextField label="ФИО" placeholder="Иванов Иван Иванович" {...register('fullName')} />
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-ink/80">Телефон</span>
            <Controller
              name="phone"
              control={control}
              render={({ field: f }) => (
                <input
                  className={inputClass}
                  inputMode="tel"
                  placeholder="+7(___)___-__-__"
                  value={f.value ?? ''}
                  onChange={(e) => f.onChange(formatPhoneRu(e.target.value))}
                />
              )}
            />
          </label>

          <div className="block text-sm">
            <span className="mb-1 block font-medium text-ink/80">Мои клиники</span>
            <ClinicMultiSelect initial={user.clinics} onChange={setClinicIds} />
          </div>

          {save.isError && (
            <p className="text-sm text-danger">{apiErrorMessage(save.error, 'Не удалось сохранить')}</p>
          )}
          {save.isSuccess && <p className="text-sm text-success">Сохранено</p>}

          <Button type="submit" disabled={save.isPending}>
            Сохранить
          </Button>
        </form>
      </Card>
    </section>
  );
}
